import React, { useEffect, useState, forwardRef } from "react";
import { IoCloseOutline } from "react-icons/io5";
import { GoGear } from "react-icons/go";
import { Switch } from "@mui/material";
import {
  gatAllSettings,
  getEnabledSettingUsers,
  parseSettingBoolean,
  parseUserIdList,
  updateSetting,
} from "../../ds/settings";
import { listUsers } from "../../ds/user";
import toast from "react-hot-toast";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";

const sameId = (left, right) => String(left) === String(right);

const settingFlag = (enabled) => (enabled ? "TRUE" : "FALSE");

const enabledUsersFromResponse = (res) => {
  if (Array.isArray(res)) return res;
  if (res?.code && res.code !== "00") return [];
  return Array.isArray(res?.data) ? res.data : [];
};

const isSelected = (selectedIds, id) =>
  selectedIds.some((item) => sameId(item, id));

const UserCheckboxList = ({
  users,
  selectedIds,
  onChange,
  loading,
  error,
}) => {
  const [query, setQuery] = useState("");
  const normalizedQuery = query.trim().toLowerCase();
  const visibleUsers = normalizedQuery
    ? users.filter((user) =>
        (user.names || user.name || "")
          .toLowerCase()
          .includes(normalizedQuery),
      )
    : users;
  const allVisibleSelected =
    visibleUsers.length > 0 &&
    visibleUsers.every((user) => isSelected(selectedIds, user.id));

  const toggleAll = () => {
    if (allVisibleSelected) {
      onChange(
        selectedIds.filter(
          (id) => !visibleUsers.some((user) => sameId(user.id, id)),
        ),
      );
      return;
    }

    const next = [...selectedIds];
    visibleUsers.forEach((user) => {
      if (!isSelected(next, user.id)) next.push(user.id);
    });
    onChange(next);
  };

  const toggleOne = (id) => {
    onChange(
      isSelected(selectedIds, id)
        ? selectedIds.filter((item) => !sameId(item, id))
        : [...selectedIds, id],
    );
  };

  return (
    <div className="mt-2 mb-3 w-full border border-gray-200 rounded-md">
      <div className="px-3 py-2 text-sm text-gray-600 border-b border-gray-200">
        Select the users this applies to
      </div>
      {!loading && !error && users.length > 0 && (
        <div className="px-3 py-2 border-b border-gray-200">
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search users"
            className="w-full h-[36px] border border-gray-300 rounded-md px-2 text-sm"
          />
        </div>
      )}
      <div className="max-h-[180px] overflow-y-auto">
        {loading && (
          <p className="px-3 py-2 text-sm text-gray-500">Loading users...</p>
        )}
        {!loading && error && (
          <p className="px-3 py-2 text-sm text-red-600">{error}</p>
        )}
        {!loading && !error && users.length === 0 && (
          <p className="px-3 py-2 text-sm text-gray-500">No users found</p>
        )}
        {!loading && !error && users.length > 0 && visibleUsers.length === 0 && (
          <p className="px-3 py-2 text-sm text-gray-500">No users found</p>
        )}
        {!loading && !error && visibleUsers.length > 0 && (
          <>
            <label className="flex items-center gap-2 px-3 py-2 border-b border-gray-100 cursor-pointer">
              <input
                type="checkbox"
                checked={allVisibleSelected}
                onChange={toggleAll}
              />
              <span className="text-sm font-medium">Select all</span>
            </label>
            {visibleUsers.map((user) => (
              <label
                key={user.id}
                className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-gray-50"
              >
                <input
                  type="checkbox"
                  checked={isSelected(selectedIds, user.id)}
                  onChange={() => toggleOne(user.id)}
                />
                <span className="text-sm">{user.names || user.name}</span>
              </label>
            ))}
          </>
        )}
      </div>
    </div>
  );
};

const SettingsModal = ({ closeModal }) => {
  const [checked, setChecked] = React.useState(null);
  const [checked2, setChecked2] = React.useState(null);
  const [checked3, setChecked3] = React.useState(null);
  const [canDelete, setCanDelete] = React.useState(false);
  const [canDeactivateTransaction, setCanDeactivateTransaction] = React.useState(false);
  const [canActivateEditTransaction, setCanActivateEditTransaction] =
    React.useState(false);
  const [endDate, setEndDate] = useState("");
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [usersError, setUsersError] = useState("");
  const [deleteUserIds, setDeleteUserIds] = useState([]);
  const [editUserIds, setEditUserIds] = useState([]);
  const [deleteLegacyAll, setDeleteLegacyAll] = useState(false);
  const [editLegacyAll, setEditLegacyAll] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    async function getSettings() {
      const [res, deleteUsersRes, editUsersRes] = await Promise.all([
        gatAllSettings(),
        getEnabledSettingUsers("CAN_DELETE_RECORD").catch(() => null),
        getEnabledSettingUsers("ACTIVATE_EDIT_TRANSACTION").catch(() => null),
      ]);
      const enabledUserIds = {};
      [...enabledUsersFromResponse(deleteUsersRes), ...enabledUsersFromResponse(editUsersRes)].forEach(
        (item) => {
          if (Array.isArray(item?.users)) {
            enabledUserIds[item.name] = item.users.map((user) => user.id);
          }
        },
      );

      if (res?.data) {
        let deleteEnabled = false;
        let editEnabled = false;
        let savedDeleteIds;
        let savedEditIds;

        res.data.forEach((setting) => {
          switch (setting.name) {
            case "CAN_DELETE_RECORD":
              deleteEnabled = parseSettingBoolean(setting.value);
              setCanDelete(deleteEnabled);
              setChecked(deleteEnabled);
              if (parseUserIdList(setting.userIds)) {
                savedDeleteIds = parseUserIdList(setting.userIds);
              }
              break;
            case "DEACTIVATE_TRANSACTION":
              setCanDeactivateTransaction(parseSettingBoolean(setting.value));
              setChecked2(parseSettingBoolean(setting.value));
              break;
            case "ACTIVATE_EDIT_TRANSACTION":
              editEnabled = parseSettingBoolean(setting.value);
              setCanActivateEditTransaction(editEnabled);
              setChecked3(editEnabled);
              if (parseUserIdList(setting.userIds)) {
                savedEditIds = parseUserIdList(setting.userIds);
              }
              break;
            case "CAN_DELETE_RECORD_USERS":
              savedDeleteIds = parseUserIdList(setting.value) || [];
              break;
            case "ACTIVATE_EDIT_TRANSACTION_USERS":
              savedEditIds = parseUserIdList(setting.value) || [];
              break;
            case "DEACTIVATE_END_DATE":
              if (setting.value) {
                const date = moment(setting.value, "YYYY-MM-DD").toDate();
                setEndDate(date);
              }
              break;
            default:
              break;
          }
        });

        if (Object.prototype.hasOwnProperty.call(enabledUserIds, "CAN_DELETE_RECORD")) {
          setDeleteUserIds(enabledUserIds.CAN_DELETE_RECORD);
        } else if (savedDeleteIds !== undefined) {
          setDeleteUserIds(savedDeleteIds);
        } else if (deleteEnabled) {
          setDeleteLegacyAll(true);
        }

        if (
          Object.prototype.hasOwnProperty.call(
            enabledUserIds,
            "ACTIVATE_EDIT_TRANSACTION",
          )
        ) {
          setEditUserIds(enabledUserIds.ACTIVATE_EDIT_TRANSACTION);
        } else if (savedEditIds !== undefined) {
          setEditUserIds(savedEditIds);
        } else if (editEnabled) {
          setEditLegacyAll(true);
        }
      }
    }
    getSettings();
  }, []);

  useEffect(() => {
    async function loadUsers() {
      try {
        setUsersLoading(true);
        const res = await listUsers();
        if (res?.code === "00" && Array.isArray(res.data)) {
          setUsers(res.data);
          setUsersError("");
        } else {
          setUsersError(res?.message || "Could not load users");
        }
      } catch (error) {
        setUsersError("Could not load users");
      } finally {
        setUsersLoading(false);
      }
    }
    loadUsers();
  }, []);

  useEffect(() => {
    if (!deleteLegacyAll || users.length === 0) return;
    setDeleteUserIds(users.map((user) => user.id));
    setDeleteLegacyAll(false);
  }, [deleteLegacyAll, users]);

  useEffect(() => {
    if (!editLegacyAll || users.length === 0) return;
    setEditUserIds(users.map((user) => user.id));
    setEditLegacyAll(false);
  }, [editLegacyAll, users]);

  const handleSubmit = async () => {
    const updates = [];

    if (canDelete) {
      const deleteEnabled = parseSettingBoolean(checked);
      updates.push({
        name: "CAN_DELETE_RECORD",
        value: settingFlag(deleteEnabled),
        userIds: deleteEnabled ? deleteUserIds : [],
      });
    }

    if (canDeactivateTransaction) {
      updates.push({ name: "DEACTIVATE_TRANSACTION", value: checked2 });

      if (endDate) {
        const dateValue = moment(endDate).format("YYYY-MM-DD");
        updates.push({ name: "DEACTIVATE_END_DATE", value: dateValue });
      }
    }

    if (canActivateEditTransaction) {
      const editEnabled = parseSettingBoolean(checked3);
      updates.push({
        name: "ACTIVATE_EDIT_TRANSACTION",
        value: settingFlag(editEnabled),
        userIds: editEnabled ? editUserIds : [],
      });
    }

    if (updates.length === 0) {
      toast.error("Change a setting before applying");
      return;
    }

    try {
      setSubmitting(true);
      for (const payload of updates) {
        const res = await updateSetting(payload);
        if (res?.code !== "00") {
          throw new Error(`Failed to update ${payload.name}`);
        }
      }

      toast.success("Settings updated successfully");
      closeModal();
      window.location.reload();
    } catch (error) {
      toast.error("Failed to update some settings");
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  const ExampleCustomInput = forwardRef(({ value, onClick }, ref) => (
    <div className="flex flex-col gap-2">
      <div
        className="example-custom-input border border-gray-300 h-[44px] w-[260px] md:w-[170px] lg:w-[230px] p-2 rounded-md"
        onClick={onClick}
        ref={ref}
      >
        <h3>{value ? value : "Select date"}</h3>
      </div>
    </div>
  ));


  return (
    <div className="flex flex-col gap-y-2 w-[640px] bg-white p-4 max-h-[80vh] overflow-y-auto">
      <div className="flex justify-between">
        <GoGear style={{ width: 26, height: 26 }} />
        <IoCloseOutline
          style={{ width: 26, height: 26, cursor: "pointer" }}
          onClick={() => closeModal()}
        />
      </div>
      <div className="flex justify-between items-center mb-3">
        <div>
          <h1 className="capitalize font-bold">Settings</h1>
        </div>
      </div>
      <div className="flex flex-col">
        <div>
          <Switch
            color="success"
            value={""}
            checked={Boolean(checked)}
            onChange={(e) => {
              setCanDelete(true);
              setChecked(e?.target?.checked);
            }}
          />

          <label>Activate Delete Feature</label>
          {checked && (
            <UserCheckboxList
              users={users}
              selectedIds={deleteUserIds}
              onChange={setDeleteUserIds}
              loading={usersLoading}
              error={usersError}
            />
          )}
        </div>
        <div>
          <Switch
            color="success"
            value={""}
            checked={Boolean(checked2)}
            onChange={(e) => {
              setCanDeactivateTransaction(true);
              setChecked2(e?.target?.checked);
            }}
          />
          <label>Deactivate Transaction Feature</label>
          {canDeactivateTransaction && (
            <div className="flex flex-col">
              <DatePicker
                selected={endDate}
                onChange={(date) => {
                  setEndDate(date);
                }}
                customInput={<ExampleCustomInput />}
                showYearDropdown
                yearDropdownItemNumber={100}
                scrollableYearDropdown
              />
            </div>
          )}
        </div>
        <div>
          <Switch
            color="success"
            value={""}
            checked={Boolean(checked3)}
            onChange={(e) => {
              setCanActivateEditTransaction(true);
              setChecked3(e?.target?.checked);
            }}
          />
          <label>Activate Edit Transaction Feature</label>
          {checked3 && (
            <UserCheckboxList
              users={users}
              selectedIds={editUserIds}
              onChange={setEditUserIds}
              loading={usersLoading}
              error={usersError}
            />
          )}
        </div>
      </div>
      <div className="flex gap-2 justify-center">
        <button
          className="mx-auto px-2 w-full h-[40px] font-normal text-xs flex justify-center items-center gap-3 disabled:cursor-not-allowed border border-pfBlack md:w-272"
          onClick={() => closeModal()}
        >
          Cancel
        </button>
        <button
          onClick={() => handleSubmit()}
          disabled={submitting}
          className="bg-green-700 text-white flex justify-center items-center h-[40px] w-full gap-2 disabled:cursor-not-allowed disabled:opacity-70"
        >
          {submitting ? "Applying..." : "Apply"}
        </button>
      </div>
    </div>
  );
};

export default SettingsModal;
